import type TextChange from "work/proposals/redlining/__diff/text-change";

/**
 * The focus rules every editable redline field shares (#6624).
 *
 * A redline field rests as its redline: what was there struck through, what is proposed inserted. That
 * view is the field's display, and it takes the focus the way an input does. Focus on it (a click, a
 * tap or Tab) opens the plain input in its place, leaving the input commits, Enter on a one-line field
 * commits and moves to the next field, and Escape puts back the value the field had when it was
 * focused. There is no edit icon to open a field and no Save or Cancel beside it.
 */

/** What an editable field's helper text says while it holds a value that cannot be committed. */
export const ESCAPE_HINT = "or press Escape to cancel";

/** Anything the reader can type into or choose with, in tab order: the fields Enter moves between. */
const NEXT_FIELD_SELECTOR = [
  "input:not([type='hidden']):not(:disabled):not([tabindex='-1'])",
  "textarea:not(:disabled):not([tabindex='-1'])",
  "select:not(:disabled):not([tabindex='-1'])",
  "[role='textbox'][tabindex='0']",
  "[role='combobox'][tabindex='0']",
].join(", ");

/**
 * The attributes that make a redline's display a field: in tab order, announced as a text box with
 * the field's label, and described as carrying a proposed change when it does. The display's own focus
 * handler, which opens the field's input, goes beside them.
 */
export function editableRedlineDisplayProps(label: string | undefined, hasProposedChange: boolean) {
  return {
    role: "textbox",
    tabIndex: 0,
    "aria-label": label,
    "aria-description": hasProposedChange ? "Has a proposed change" : undefined,
  };
}

/** Whether a redline's diff carries a change: anything struck through or inserted. */
export function hasProposedChange(textChanges: readonly TextChange[] | undefined): boolean {
  return Boolean(textChanges?.some((change) => change.isAdded || change.isRemoved));
}

/**
 * Moves the focus to the first field after `from` in the document, outside `from` itself, which is
 * what Enter does in a one-line field. With no field after it, the focus just leaves. Either way the
 * focus leaves `from` — the field, or the group of inputs that make one field — and that blur is what
 * commits it.
 */
export function focusNextField(from: HTMLElement): void {
  const fields = Array.from(from.ownerDocument.querySelectorAll<HTMLElement>(NEXT_FIELD_SELECTOR));
  const next = fields.find(
    (field) =>
      !from.contains(field) &&
      Boolean(from.compareDocumentPosition(field) & Node.DOCUMENT_POSITION_FOLLOWING) &&
      !field.closest("[aria-hidden='true'], [hidden]"),
  );
  if (next) {
    next.focus();
    return;
  }
  const focused = from.ownerDocument.activeElement;
  if (focused instanceof HTMLElement && from.contains(focused)) focused.blur();
}

/**
 * Gives the focus back to a field whose value cannot be committed, once the blur that took it away
 * has finished: the field keeps the focus, with its error, until it holds a value or Escape reverts it.
 */
export function keepFocusOn(field: HTMLElement): void {
  globalThis.setTimeout(() => {
    if (field.isConnected) field.focus();
  }, 0);
}

/** Puts the caret after the last character of an input that has just taken the focus. */
export function placeCaretAtEnd(input: HTMLInputElement | HTMLTextAreaElement, suffixLength = 0): void {
  const end = Math.max(0, input.value.length - suffixLength);
  input.setSelectionRange(end, end);
}
